import { useState, useCallback, RefObject } from 'react';

import type { FormatState } from '../types';

const INITIAL_FORMAT_STATE: FormatState = {
  bold: false,
  italic: false,
  underline: false,
  link: false,
  unorderedList: false,
  orderedList: false,
};

interface UseToolbarOptions {
  editorRef: RefObject<HTMLDivElement | null>;
  isSelectionInLink: () => boolean;
}

/**
 * Hook for managing toolbar format state based on the current selection.
 */
export const useToolbar = ({ editorRef, isSelectionInLink }: UseToolbarOptions) => {
  const [formatState, setFormatState] = useState<FormatState>(INITIAL_FORMAT_STATE);

  const updateFormatState = useCallback(() => {
    setFormatState({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      link: isSelectionInLink(),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      orderedList: document.queryCommandState('insertOrderedList'),
    });
  }, [isSelectionInLink]);

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !editorRef.current) return;

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range || !editorRef.current.contains(range.commonAncestorContainer)) return;

    setTimeout(updateFormatState, 10);
  }, [editorRef, updateFormatState]);

  return {
    formatState,
    updateFormatState,
    handleSelectionChange,
  };
};
